// Sentry browser init. Imported first in main.tsx so every subsequent module
// is instrumented. No-op when VITE_SENTRY_DSN isn't set, so dev + preview
// builds don't need any Sentry config.
import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  const environment =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    (import.meta.env.MODE === 'production' ? 'production' : 'development');

  const tracesSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) || '0.1',
  );
  const replaysSessionSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined) || '0',
  );
  const replaysOnErrorSampleRate = parseFloat(
    (import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined) || '1.0',
  );

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Claim and payment routes contain names, addresses, vehicle details,
        // evidence analysis and document previews. Replay must never expose them.
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
    replaysSessionSampleRate: Number.isFinite(replaysSessionSampleRate) ? replaysSessionSampleRate : 0,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate) ? replaysOnErrorSampleRate : 1.0,
    // Same-origin API calls need trace propagation; anything else is third-party.
    tracePropagationTargets: getTracePropagationTargets(),
    // Environmental noise we can't action and that the app now handles in-UI:
    // "Failed to load Stripe.js" fires when an ad/privacy blocker or flaky
    // network blocks js.stripe.com. The payment modal already shows a friendly
    // message in that case, so there's nothing to triage here.
    ignoreErrors: ['Failed to load Stripe.js'],
    beforeSend: (event) => (isReplayCrossOriginFrameError(event) ? null : event),
  });
  if (import.meta.env.DEV) {
    console.log(`[Sentry] initialised (env=${environment})`);
  }
}

// Replay's iframe observer throws a SecurityError when it touches a
// cross-origin iframe injected into the page by a browser extension (e.g.
// #pai-widget-iframe, FIXTYER-FRONTEND-6). The app itself never touches
// iframes, so a cross-origin frame error from an auto-wrapped event listener
// is never ours. Stack filenames can't be used here: beforeSend sees minified
// bundle URLs, not the source-mapped @sentry paths shown in the dashboard.
export function isReplayCrossOriginFrameError(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  return (
    exception?.type === 'SecurityError' &&
    (exception.value?.includes('cross-origin frame') ?? false) &&
    (exception.mechanism?.type?.startsWith('auto.browser.browserapierrors') ?? false)
  );
}

function getTracePropagationTargets(): (RegExp | string)[] {
  const siteUrl =
    (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://fixtyer.co.uk';
  try {
    const host = new URL(siteUrl).host.replace(/^www\./, '');
    const escapedHost = host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return [
      /^\//,
      new RegExp(`^https://${escapedHost}/`),
      new RegExp(`^https://www\\.${escapedHost}/`),
    ];
  } catch {
    return [/^\//, /^https:\/\/fixtyer\.co\.uk\//, /^https:\/\/www\.fixtyer\.co\.uk\//];
  }
}

export { Sentry };
